import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { formatFieldValue } from "src/util/util.service";
import { App, Modal, Tabs, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomTypography } from "sipli-fleet-core/components";
import {
  MAPPING_FILTER_ICON,
  MAPPING_RIGHT_ARROW_ICON,
  MAPPING_REMOVE_FILTER_ICON,
} from "src/assets/svgs";
import { useMutationSendCommand } from "src/queries/commands.queries";
import type { IncomingValueItem } from "src/services/mappingService";
import { cn } from "../../../../../../sipli-fleet-core/utils/cn";
import { getMappingFilters } from "./mapping.helpers";
import type { MappingFilterRecord, MappingRecord } from "./mapping.types";
import { useEpochFormatter } from "../../../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";

interface MappingSingleCardProps {
  title: string;
  value: string;
  updatedAt: string;
  receivedAt: string;
  recentData?: IncomingValueItem[];
  mappings: MappingRecord[];
  vehicleId?: string | number;
  commandId?: number;
  enableAddMappingAction?: boolean;
  onSettingsClick: () => void;
  onDetailsClick: () => void;
}

const applySavedFilter = (filter: MappingFilterRecord, currentValue: unknown) => {
  if (currentValue === "") return currentValue;

  const numericValue = Number(currentValue);
  if (Number.isNaN(numericValue)) return currentValue;

  const fields = filter.fields ?? {};

  switch (filter.id) {
    case "adjust_value_offset_multiply":
      return (numericValue + Number(fields.offset ?? 0)) * (Number(fields.multiplier) || 1);
    case "adjust_value_if_statement": {
      const param = Number(fields.param ?? 0);
      const operator = fields.operator;
      const matches =
        operator === "greater_than"
          ? numericValue > param
          : operator === "greater_than_equal"
            ? numericValue >= param
            : operator === "less_than"
              ? numericValue < param
              : operator === "less_than_equal"
                ? numericValue <= param
                : operator === "equal"
                  ? numericValue === param
                  : false;

      return matches ? Number(fields.transformedValue ?? 0) : numericValue;
    }
    case "invert_value":
      return numericValue === 0 ? 1 : 0;
    case "allowed_range_inclusive": {
      const minValue = Number(fields.minValue ?? 0);
      const maxValue = Number(fields.maxValue ?? 0);
      return numericValue >= minValue && numericValue <= maxValue ? numericValue : "--";
    }
    default:
      return currentValue;
  }
};

const applySavedFilters = (filters: MappingFilterRecord[] | undefined, rawValue: unknown) =>
  filters?.reduce<unknown>((result, filter) => applySavedFilter(filter, result), rawValue) ??
  rawValue;

const getRecentValue = (rawValue: unknown) => {
  if (!rawValue || typeof rawValue !== "object") return rawValue;

  const valueRecord = rawValue as Record<string, unknown>;
  return valueRecord.value ?? valueRecord.lastValue ?? valueRecord.data ?? rawValue;
};

const getRecentValueText = (rawValue: unknown) => {
  const value = getRecentValue(rawValue);

  if (value === null || value === undefined) return "--";

  const text = String(value);
  const valueMatch = text.match(/\bValue\s*:\s*([^\s]+)/i);

  return valueMatch?.[1] ?? text;
};

const MappingSingleCard = ({
  title,
  value,
  updatedAt,
  receivedAt,
  recentData = [],
  mappings,
  vehicleId,
  commandId = 28,
  enableAddMappingAction = false,
  onSettingsClick,
  onDetailsClick,
}: MappingSingleCardProps) => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const { mutateAsync: sendCommand, isPending: isSendingRelayCommand } = useMutationSendCommand();
  const [isFilteredValue, setIsFilteredValue] = useState(false);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const hasMapping = mappings.length > 0;
  const relayMapping = useMemo(
    () => mappings.find((mapping) => mapping.mappingTypeId === "relay_status"),
    [mappings]
  );
  const relayFields = relayMapping?.mappingFields ?? {};
  const relayId = relayFields.relayId ?? "1";
  const relayLabel = String(relayFields.relayLabel ?? "");
  const isRelayOn = value === "1";
  const relayValueLabel = String(
    isRelayOn ? relayFields.valueOnLabel || "ACTIVATE" : relayFields.valueOffLabel || "DEACTIVATE"
  );
  const showRelayAction = Boolean(relayMapping && isFilteredValue && vehicleId);
  const activeFilters = useMemo(
    () => {
      const mappingWithFilters = mappings.find((mapping) => getMappingFilters(mapping).length);
      return mappingWithFilters ? getMappingFilters(mappingWithFilters) : undefined;
    },
    [mappings]
  );
  const canToggleFilteredValue = Boolean(activeFilters?.length) || Boolean(relayMapping);
  const filteredValue = useMemo(
    () => applySavedFilters(activeFilters, value),
    [activeFilters, value]
  );
  const displayedValue = isFilteredValue ? filteredValue : value;
  const displayedValueText = formatFieldValue(displayedValue, title, i18n.language);
  const canOpenValueModal = displayedValueText.length > 20;
  const cardValueText = canOpenValueModal
    ? `${displayedValueText.substring(0, 20)}...`
    : displayedValueText;
  const recentItems = recentData.slice(0, 3);
  const emptyText = t("no_data", { defaultValue: "No data" });
  const chartItems = recentItems;
  const formatEpoch = useEpochFormatter();
  const chartValues = chartItems.map((item) => {
    const numericValue = Number(getRecentValueText(item.value));
    return Number.isNaN(numericValue) ? 0 : numericValue;
  });
  const chartMaxValue = Math.max(...chartValues, 0);
  const positiveChartValues = chartValues.filter((chartValue) => chartValue > 0);
  const chartMinValue = positiveChartValues.length ? Math.min(...positiveChartValues) : 0;
  const chartValueRange = chartMaxValue - chartMinValue;
  const closeValueMinBarWidthPercent = 96;
  const minBarWidthPercent = 8;

  useEffect(() => {
    if (!canToggleFilteredValue) setIsFilteredValue(false);
  }, [canToggleFilteredValue]);

  const truncateValue = (rawValue: unknown, length = 4) => {
    const text = rawValue === null || rawValue === undefined ? "--" : String(rawValue);
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const renderDateValue = (date?: string) => (
    <span className="mt-1.5 block w-20 break-words !text-[12.44px] !font-medium !leading-[15px] !text-gray9">
      {date || "--"}
    </span>
  );

  const handleRelayCommand = () => {
    if (!vehicleId || isSendingRelayCommand) return;

    const nextValue = isRelayOn ? "0" : "1";
    const nextAction = isRelayOn ? "OFF" : "ON";

    modal.confirm({
      title: t("confirmRelayAction", { defaultValue: "Confirm Relay Action" }),
      content: t("confirmRelayActionMessage", {
        action: nextAction,
        defaultValue: `Are you sure you want to turn ${nextAction} this relay?`,
      }),
      okText: t("confirm", { defaultValue: "Confirm" }),
      cancelText: t("cancel", { defaultValue: "Cancel" }),
      onOk: async () => {
        try {
          await sendCommand({
            vehicleId,
            payload: {
              commandId,
              params: {
                doutId: String(relayId || "1"),
                seconds: "0",
                value: nextValue,
              },
              scheduledCommand: false,
            },
          });
          message.success(
            t("commandSentSuccessfully", { defaultValue: "Command sent successfully" })
          );
        } catch {
          message.error(t("command_submit_failed", { defaultValue: "Failed to send command" }));
        }
      },
    });
  };

  return (
    <div
      className={cn("h-[233px] w-[331px] rounded-[16px] border-primary bg-gray17 p-[16px]", {
        "border-2": hasMapping,
        "border-0": !hasMapping,
      })}
    >
      <div className="mb-[8px]">
        <CustomTypography.CustomText className="block break-words !text-[15.75px] !font-[600] !leading-[20px] !text-gray10">
          {title}
        </CustomTypography.CustomText>
      </div>
      <div className="mb-[16px] flex items-center gap-[10px]">
        <button
          type="button"
          disabled={!canOpenValueModal}
          onClick={() => setIsValueModalOpen(true)}
          className="border-0 bg-transparent p-0 text-left text-[17.72px] font-bold leading-[28px] text-black disabled:cursor-default enabled:cursor-pointer enabled:hover:underline"
        >
          {cardValueText}
        </button>
        <Tooltip
          title={
            canToggleFilteredValue
              ? isFilteredValue
                ? t("remove_filter")
                : t("filter_data")
              : t("noFilters")
          }
        >
          <button
            type="button"
            disabled={!canToggleFilteredValue}
            onClick={() => setIsFilteredValue((prev) => !prev)}
            className="h-[24px] w-[25px] cursor-pointer flex items-center justify-center bg-transparent hover:bg-primary5 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {isFilteredValue ? (
              <img src={MAPPING_REMOVE_FILTER_ICON} alt="Remove Filter" />
            ) : (
              <img src={MAPPING_FILTER_ICON} alt="Filter" />
            )}
          </button>
        </Tooltip>
        {showRelayAction && (
          <Tooltip title={relayLabel}>
            <button
              type="button"
              disabled={isSendingRelayCommand}
              onClick={handleRelayCommand}
              className={cn(
                "max-w-[130px] min-h-[28px] cursor-pointer rounded-[6px] border-0 bg-[#9e9e9e] px-[9px] py-[4px] text-[12px] font-semibold leading-[14px] text-white whitespace-nowrap hover:bg-[#757575] disabled:cursor-wait disabled:opacity-70",
                {
                  "bg-primary hover:bg-[#2a56c7]": isRelayOn,
                }
              )}
            >
              {truncateValue(relayValueLabel, 14)}
            </button>
          </Tooltip>
        )}
      </div>

      <div className="mb-[16px] h-[94px] w-[299px] rounded-[8px] border border-gray3 bg-white p-[8px]">
        <Tabs
          defaultActiveKey="last"
          tabBarGutter={8}
          className="mapping-card-tabs"
          items={[
            {
              key: "last",
              label: t("lastData", { defaultValue: "Last data" }),
              children: (
                <div className="grid grid-cols-2 gap-[18px]">
                  <div>
                    <CustomTypography.CustomText className="block !text-[12.44px] !font-medium !leading-[100%] !text-black">
                      {t("receivedOn", { defaultValue: "Received on" })}:
                    </CustomTypography.CustomText>
                    {renderDateValue(receivedAt)}
                  </div>
                  <div className="border-l border-[#D4D8E2] pl-[18px]">
                    <CustomTypography.CustomText className="block !text-[12.44px] !font-medium !leading-[100%] !text-black">
                      {t("updatedOn", { defaultValue: "Updated on" })}:
                    </CustomTypography.CustomText>
                    {renderDateValue(updatedAt)}
                  </div>
                </div>
              ),
            },
            {
              key: "last3",
              label: t("last3Data", { defaultValue: "Last 3 data" }),
              children: chartItems.length ? (
                <div className="flex min-h-[49px] flex-col justify-center gap-[2px] pb-[6px]">
                  {chartItems.map((item, index) => {
                    const barValue = chartValues[index] ?? 0;
                    const barWidth = (() => {
                      if (barValue <= 0 || chartMaxValue <= 0) return "4px";
                      if (chartValueRange === 0) return "100%";

                      const valueRangeRatio = chartValueRange / chartMaxValue;

                      if (valueRangeRatio <= 0.05) {
                        const rangePercent =
                          ((barValue - chartMinValue) / chartValueRange) *
                          (100 - closeValueMinBarWidthPercent);

                        return `${closeValueMinBarWidthPercent + rangePercent}%`;
                      }

                      const barWidthPercent = (barValue / chartMaxValue) * 100;
                      return `${Math.max(barWidthPercent, minBarWidthPercent)}%`;
                    })();
                    const valueText = formatFieldValue(getRecentValueText(item.value), title, i18n.language);
                    const tooltipContent = (
                      <div>
                        <div>
                          {t("value")}: {valueText}
                        </div>
                        <div>
                          {t("lastUpdate")}: {formatEpoch(item.lastUpdate, "HH:mm:ss")}
                        </div>
                        <div>
                          {t("receivedAt")}: {formatEpoch(item.receivedAt, "HH:mm:ss")}
                        </div>
                      </div>
                    );

                    return (
                      <div
                        key={`${item.lastUpdate ?? ""}-${item.receivedAt ?? ""}-${index}`}
                        className="grid h-[15px] grid-cols-[1fr_42px] items-center gap-[8px]"
                      >
                        <div className="h-[12px] min-w-0">
                          <Tooltip title={tooltipContent} arrow>
                            <span
                              className="block h-full bg-gray3 transition-colors duration-150 hover:bg-gray5"
                              style={{ width: barWidth }}
                            />
                          </Tooltip>
                        </div>
                        <Tooltip title={valueText.length > 8 ? valueText : ""}>
                          <span className="block truncate text-right text-[12.44px] font-medium leading-[100%] text-black">
                            {valueText}
                          </span>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex min-h-[49px] items-center text-[14px] text-gray9">
                  {emptyText}
                </div>
              ),
            },
          ]}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={!enableAddMappingAction}
          onClick={onSettingsClick}
          className={cn(
            "text-[14px] font-[600] leading-[18px] text-primary underline",
            enableAddMappingAction ? "cursor-pointer" : "cursor-default"
          )}
        >
          {t("addMapping")}
        </button>
        <button
          type="button"
          disabled={!hasMapping}
          onClick={onDetailsClick}
          className="flex min-w-0 cursor-pointer items-center gap-[6px] disabled:cursor-text"
        >
          <CustomTag
            interactive={mappings.length > 0}
            className={cn("bg-transparent text-gray8", {
              "bg-primary text-white flex":
                mappings.length > 0,
            })}
          >
            {formatNumber(mappings.length, i18n.language)}
          </CustomTag>
          <span
            className={cn("text-[14px] text-gray8 font-medium", {
              "font-[600] leading-[18px] text-primary underline": mappings.length > 0,
            })}
          >
            {/* {t("mapping", { defaultValue: "mapping" })} */}
            mapping
          </span>
          {mappings.length > 0 && <img src={MAPPING_RIGHT_ARROW_ICON} alt="" className="" />}
        </button>
      </div>
      <Modal
        open={isValueModalOpen}
        footer={null}
        centered
        closable={false}
        onCancel={() => setIsValueModalOpen(false)}
      >
        <p className="m-0 whitespace-pre-wrap break-words text-[16px] font-semibold leading-6 text-black">
          {displayedValueText}
        </p>
      </Modal>
    </div>
  );
};

export default MappingSingleCard;
